// 2026-11-03: Admin › Services (api/src/services.ts): every outside service's status, this month's usage and spend,
// and its budget. Types, the normaliser, and the small words the page uses.

export type ServiceCategory = "ai" | "infra" | "business";
export type ServiceState = "ok" | "degraded" | "down" | "unconfigured" | "error";
export type StatusIndicator = "none" | "minor" | "major" | "critical" | "maintenance" | "unknown";
export type SpendSource = "provider" | "ledger" | "plan" | "estimate" | "none";

export type ServiceView = {
  id: string;
  name: string;
  category: ServiceCategory;
  role: string;
  state: ServiceState;
  summary: string;
  ping: { ok: boolean; ms: number | null; error: string | null } | null;
  statusPage: { indicator: StatusIndicator; description: string; url: string } | null;
  usage: { label: string; value: string; pct: number | null }[];
  spend: { monthUsd: number | null; source: SpendSource; providerUsd: number | null; ledgerUsd: number; planUsd: number | null; projectedUsd: number | null; note: string | null };
  balance: { label: string; usd: number | null } | null;
  budget: { usd: number | null; pct: number | null; level: "ok" | "near" | "over" | null };
  needs: string | null;
  dashboardUrl: string;
  error: string | null;
};

export type ServicesView = {
  month: { start: string; end: string; dayOfMonth: number; daysInMonth: number };
  totals: { monthUsd: number; projectedUsd: number; budgetUsd: number; ledgerUsd: number; revenueUsd: number | null; issues: number };
  services: ServiceView[];
  checkedAt: string;
  cached: boolean;
};

export type ServiceBudgetPatch = Record<string, { budgetUsd?: number | null; planUsd?: number | null }>;

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === "object" && !Array.isArray(v);
const str = (v: unknown) => (typeof v === "string" ? v : "");
const num = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : 0);
const numOr = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : null);
const pick = <T extends string>(v: unknown, all: readonly T[], d: T): T => (all.includes(v as T) ? (v as T) : d);

export const CATEGORIES: { id: ServiceCategory; title: string }[] = [
  { id: "ai", title: "AI providers" },
  { id: "infra", title: "Infrastructure" },
  { id: "business", title: "Business services" },
];

function normService(v: unknown): ServiceView | null {
  if (!isObj(v) || !str(v.id)) return null;
  const sp = isObj(v.statusPage) ? v.statusPage : null;
  const p = isObj(v.ping) ? v.ping : null;
  const s = isObj(v.spend) ? v.spend : {};
  const b = isObj(v.budget) ? v.budget : {};
  const bal = isObj(v.balance) ? v.balance : null;
  return {
    id: str(v.id),
    name: str(v.name) || str(v.id),
    category: pick(v.category, ["ai", "infra", "business"] as const, "business"),
    role: str(v.role),
    state: pick(v.state, ["ok", "degraded", "down", "unconfigured", "error"] as const, "error"),
    summary: str(v.summary),
    ping: p ? { ok: p.ok === true, ms: numOr(p.ms), error: str(p.error) || null } : null,
    statusPage: sp ? { indicator: pick(sp.indicator, ["none", "minor", "major", "critical", "maintenance", "unknown"] as const, "unknown"), description: str(sp.description), url: str(sp.url) } : null,
    usage: (Array.isArray(v.usage) ? v.usage : []).filter(isObj).map((u) => ({ label: str(u.label), value: str(u.value), pct: numOr(u.pct) })),
    spend: {
      monthUsd: numOr(s.monthUsd),
      source: pick(s.source, ["provider", "ledger", "plan", "estimate", "none"] as const, "none"),
      providerUsd: numOr(s.providerUsd),
      ledgerUsd: num(s.ledgerUsd),
      planUsd: numOr(s.planUsd),
      projectedUsd: numOr(s.projectedUsd),
      note: str(s.note) || null,
    },
    balance: bal ? { label: str(bal.label), usd: numOr(bal.usd) } : null,
    budget: { usd: numOr(b.usd), pct: numOr(b.pct), level: b.level === "ok" || b.level === "near" || b.level === "over" ? b.level : null },
    needs: str(v.needs) || null,
    dashboardUrl: str(v.dashboardUrl),
    error: str(v.error) || null,
  };
}

export function normServices(raw: unknown): ServicesView {
  const o = isObj(raw) && isObj(raw.services) ? raw.services : isObj(raw) ? raw : {};
  const m = isObj(o.month) ? o.month : {};
  const t = isObj(o.totals) ? o.totals : {};
  return {
    month: { start: str(m.start), end: str(m.end), dayOfMonth: num(m.dayOfMonth), daysInMonth: num(m.daysInMonth) || 30 },
    totals: { monthUsd: num(t.monthUsd), projectedUsd: num(t.projectedUsd), budgetUsd: num(t.budgetUsd), ledgerUsd: num(t.ledgerUsd), revenueUsd: numOr(t.revenueUsd), issues: num(t.issues) },
    services: (Array.isArray(o.services) ? o.services : []).map(normService).filter((x): x is ServiceView => !!x),
    checkedAt: str(o.checkedAt),
    cached: o.cached === true,
  };
}

// ------------------------------------------------------------------ words

export const STATE_WORD: Record<ServiceState, string> = { ok: "Up", degraded: "Degraded", down: "Down", error: "Problem", unconfigured: "Not connected" };
export const SOURCE_WORD: Record<SpendSource, string> = { provider: "their bill", ledger: "our recorded calls", plan: "plan price", estimate: "estimate", none: "" };
export const INDICATOR_WORD: Record<StatusIndicator, string> = { none: "Operational", minor: "Minor issues", major: "Major outage", critical: "Critical outage", maintenance: "Maintenance", unknown: "Status page" };

export const usd = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** "just now", "4 min ago", "2 h ago" */
export function ago(iso: string, now = Date.now()): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return "";
  const s = Math.max(0, Math.round((now - t) / 1000));
  if (s < 45) return "just now";
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  return `${Math.round(s / 3600)} h ago`;
}

/** Problems first, then what costs most, then the rest by name. */
export function sortServices(list: ServiceView[]): ServiceView[] {
  const rank = (s: ServiceView) => (s.state === "down" ? 0 : s.state === "error" ? 1 : s.budget.level === "over" ? 2 : s.state === "degraded" ? 3 : s.budget.level === "near" ? 4 : s.state === "unconfigured" ? 9 : 5);
  return [...list].sort((a, b) => rank(a) - rank(b) || (b.spend.monthUsd ?? -1) - (a.spend.monthUsd ?? -1) || a.name.localeCompare(b.name));
}

/** A dollar amount typed in a budget box: null clears it, NaN is a typo. */
export function parseUsd(s: string): number | null {
  const t = s.replace(/[$,\s]/g, "");
  if (!t) return null;
  const v = Number(t);
  return Number.isFinite(v) && v >= 0 ? Math.round(v * 100) / 100 : NaN;
}
